import { useState, useEffect } from "react";
import API from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Search, ScrollText, Filter } from "lucide-react";

const entityColors = {
  login: "bg-blue-50 text-blue-700",
  vendor: "bg-emerald-50 text-emerald-700",
  request: "bg-indigo-50 text-indigo-700",
  approval: "bg-amber-50 text-amber-700",
  user: "bg-purple-50 text-purple-700",
  location: "bg-cyan-50 text-cyan-700",
  system_config: "bg-zinc-100 text-zinc-600",
  document: "bg-teal-50 text-teal-700",
  notification: "bg-rose-50 text-rose-700",
};

export default function AuditLogs() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterEntity, setFilterEntity] = useState("all");
  const [filterRole, setFilterRole] = useState("all");

  const fetchLogs = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.set("search", search);
      if (filterEntity !== "all") params.set("entity_type", filterEntity);
      if (filterRole !== "all") params.set("actor_role", filterRole);
      params.set("limit", "50");
      const { data } = await API.get(`/management/audit-logs?${params}`);
      setLogs(data.logs);
      setTotal(data.total);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLogs(); }, [search, filterEntity, filterRole]);

  return (
    <div className="space-y-4" data-testid="audit-logs-page">
      <div>
        <h1 className="text-2xl font-heading font-bold text-zinc-900">Audit Logs</h1>
        <p className="text-sm text-zinc-500">{total} log entries</p>
      </div>

      {/* Filters */}
      <Card className="border border-zinc-200">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
              <Input placeholder="Search logs..." value={search} onChange={e => setSearch(e.target.value)}
                className="pl-9" data-testid="audit-search-input" />
            </div>
            <Select value={filterEntity} onValueChange={setFilterEntity}>
              <SelectTrigger className="w-full sm:w-40" data-testid="filter-entity">
                <SelectValue placeholder="Entity" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Entities</SelectItem>
                <SelectItem value="login">Login</SelectItem>
                <SelectItem value="vendor">Technician</SelectItem>
                <SelectItem value="request">Request</SelectItem>
                <SelectItem value="approval">Approval</SelectItem>
                <SelectItem value="user">User</SelectItem>
                <SelectItem value="location">Location</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterRole} onValueChange={setFilterRole}>
              <SelectTrigger className="w-full sm:w-40" data-testid="filter-role">
                <SelectValue placeholder="Role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Roles</SelectItem>
                <SelectItem value="vendor">Technician</SelectItem>
                <SelectItem value="dispatcher">Dispatcher</SelectItem>
                <SelectItem value="manager">Manager</SelectItem>
                <SelectItem value="branch_manager">Branch Manager</SelectItem>
                <SelectItem value="owner">Owner</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border border-zinc-200">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs">Timestamp</TableHead>
                <TableHead className="text-xs">Action</TableHead>
                <TableHead className="text-xs">Entity</TableHead>
                <TableHead className="text-xs hidden md:table-cell">Actor Role</TableHead>
                <TableHead className="text-xs hidden lg:table-cell">Source</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={5} className="text-center py-8 text-zinc-400">Loading...</TableCell></TableRow>
              ) : logs.length === 0 ? (
                <TableRow><TableCell colSpan={5} className="text-center py-8 text-zinc-400">No logs found</TableCell></TableRow>
              ) : logs.map(log => (
                <TableRow key={log.id} data-testid={`audit-row-${log.id}`}>
                  <TableCell className="text-xs text-zinc-500 whitespace-nowrap">
                    {new Date(log.created_at).toLocaleString()}
                  </TableCell>
                  <TableCell>
                    <p className="text-sm text-zinc-900">{log.action_label}</p>
                    <p className="text-[10px] text-zinc-400 font-mono">{log.action_type}</p>
                  </TableCell>
                  <TableCell>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${entityColors[log.entity_type] || "bg-zinc-100 text-zinc-600"}`}>
                      {log.entity_type}
                    </span>
                  </TableCell>
                  <TableCell className="hidden md:table-cell">
                    <span className="text-xs text-zinc-600 capitalize">{log.actor_role || "—"}</span>
                  </TableCell>
                  <TableCell className="hidden lg:table-cell">
                    <span className="text-xs text-zinc-400">{log.app_source?.replace("_", " ") || "—"}</span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}
